import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Loader2, Save, Trash2, Plus, Mail, Send, Eye, EyeOff, CheckCircle, XCircle, DollarSign, AlertTriangle, Ban, History, Users, CloudUpload } from 'lucide-react';
import SocialMediaSettings from './SocialMediaSettings';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    reading_website_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genres, setGenres] = useState([]);
  const [newGenre, setNewGenre] = useState('');
  const [addingGenre, setAddingGenre] = useState(false);
  const [deletingGenre, setDeletingGenre] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [resettingBooks, setResettingBooks] = useState(false);
  const [migratingOriginals, setMigratingOriginals] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    api_key: '',
    domain: '',
    sender_email: '',
    region: 'us'
  });
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  // Cloudinary credentials are now permanently configured in .env
  
  // Registration limits state
  const [registrationLimits, setRegistrationLimits] = useState({
    max_proofreaders: '',
    max_translators: '',
    current_proofreaders: 0,
    current_translators: 0
  });
  const [savingLimits, setSavingLimits] = useState(false);
  
  // Payment rates state
  const [paymentRates, setPaymentRates] = useState({
    proofreader_rate_per_error: 2.0,
    translator_min_rate: 150.0,
    translator_max_rate: 300.0,
    translator_min_pages: 50,
    translator_max_pages: 200,
    translator_deduction_per_error: 2.0,
    max_faults_allowed: 83,
    rejection_threshold: 3,
    admin_monthly_payment: 0.0
  });
  const [savingPaymentRates, setSavingPaymentRates] = useState(false);
  const [loadingPaymentRates, setLoadingPaymentRates] = useState(true);
  
  // Audit trail state
  const [auditHistory, setAuditHistory] = useState([]);
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // Earnings settings state
  const [earningsSettings, setEarningsSettings] = useState({
    coin_to_rand_rate: 0.25,
    google_apple_percentage: 10.0,
    app_percentage: 30.0,
    writer_percentage: 60.0
  });
  const [savingEarnings, setSavingEarnings] = useState(false);
  const [loadingEarnings, setLoadingEarnings] = useState(true);

  useEffect(() => {
    fetchCurrentUser();
    fetchSettings();
    fetchGenres();
    fetchEmailSettings();
    fetchPaymentRates();
    fetchRegistrationLimits();
    fetchEarningsSettings();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Kon nie instellings laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`);
      setEmailConfigured(response.data.configured);
      if (response.data.configured) {
        setEmailSettings({
          api_key: '', // Never show the actual key
          domain: response.data.domain || '',
          sender_email: response.data.sender_email || '',
          region: response.data.region || 'us'
        });
      }
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    }
  };

  const fetchPaymentRates = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-rates`);
      setPaymentRates(response.data);
    } catch (error) {
      console.error('Failed to fetch payment rates:', error);
      // Keep default values if fetch fails
    } finally {
      setLoadingPaymentRates(false);
    }
  };

  const handleSavePaymentRates = async () => {
    setSavingPaymentRates(true);
    try {
      await axios.post(`${API}/admin/payment-rates`, paymentRates);
      toast.success('Betalingstariewe gestoor!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie betalingstariewe stoor nie');
    } finally {
      setSavingPaymentRates(false);
    }
  };

  const handlePaymentRateChange = (field, value) => {
    // Parse numeric values
    let parsedValue = value;
    if (['proofreader_rate_per_error', 'translator_min_rate', 'translator_max_rate', 
         'translator_deduction_per_error', 'admin_monthly_payment'].includes(field)) {
      parsedValue = parseFloat(value) || 0;
    } else if (['translator_min_pages', 'translator_max_pages', 'max_faults_allowed', 'rejection_threshold'].includes(field)) {
      parsedValue = parseInt(value) || 0;
    }
    setPaymentRates(prev => ({ ...prev, [field]: parsedValue }));
  };

  // Registration limits functions
  const fetchRegistrationLimits = async () => {
    try {
      const response = await axios.get(`${API}/admin/registration-limits`);
      setRegistrationLimits({
        max_proofreaders: response.data.max_proofreaders ?? '',
        max_translators: response.data.max_translators ?? '',
        current_proofreaders: response.data.current_proofreaders || 0,
        current_translators: response.data.current_translators || 0
      });
    } catch (error) {
      console.error('Failed to fetch registration limits:', error);
    }
  };

  const handleSaveRegistrationLimits = async () => {
    setSavingLimits(true);
    try {
      await axios.post(`${API}/admin/registration-limits`, {
        max_proofreaders: registrationLimits.max_proofreaders === '' ? null : parseInt(registrationLimits.max_proofreaders),
        max_translators: registrationLimits.max_translators === '' ? null : parseInt(registrationLimits.max_translators)
      });
      toast.success('Registrasie limiete gestoor!');
      fetchRegistrationLimits(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie limiete stoor nie');
    } finally {
      setSavingLimits(false);
    }
  };

  // Audit trail functions
  const fetchAuditHistory = async () => {
    setLoadingAudit(true);
    try {
      const response = await axios.get(`${API}/admin/payment-rates-history`);
      setAuditHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  const toggleAuditHistory = () => {
    if (!showAuditHistory && auditHistory.length === 0) {
      fetchAuditHistory();
    }
    setShowAuditHistory(!showAuditHistory);
  };

  const formatFieldName = (field) => {
    const names = {
      'proofreader_rate_per_error': 'Proefleser Tarief per Fout',
      'translator_min_rate': 'Vertaler Min. Tarief',
      'translator_max_rate': 'Vertaler Maks. Tarief',
      'translator_min_pages': 'Vertaler Min. Bladsye',
      'translator_max_pages': 'Vertaler Maks. Bladsye',
      'translator_deduction_per_error': 'Vertaler Aftrekking per Fout',
      'max_faults_allowed': 'Maks. Foute per Boek',
      'rejection_threshold': 'Afkeur Drempel',
      'admin_monthly_payment': 'Admin Maandelikse Betaling'
    };
    return names[field] || field;
  };

  // Earnings settings functions
  const fetchEarningsSettings = async () => {
    setLoadingEarnings(true);
    try {
      const response = await axios.get(`${API}/admin/earnings-settings`);
      setEarningsSettings(response.data || {
        coin_to_rand_rate: 0.25,
        google_apple_percentage: 10.0,
        app_percentage: 30.0,
        writer_percentage: 60.0
      });
    } catch (error) {
      console.error('Failed to fetch earnings settings:', error);
    } finally {
      setLoadingEarnings(false);
    }
  };

  const handleSaveEarningsSettings = async () => {
    // Validate percentages sum to 100
    const total = parseFloat(earningsSettings.google_apple_percentage) + 
                  parseFloat(earningsSettings.app_percentage) + 
                  parseFloat(earningsSettings.writer_percentage);
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Persentasies moet 100% wees. Huidig: ${total.toFixed(1)}%`);
      return;
    }
    
    setSavingEarnings(true);
    try {
      await axios.post(`${API}/admin/earnings-settings`, earningsSettings);
      toast.success('Verdienste instellings gestoor!');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Kon nie stoor nie';
      toast.error(errorMessage);
    } finally {
      setSavingEarnings(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres`);
      // API returns array directly, not wrapped in {genres: [...]}
      setGenres(response.data || []);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
      setGenres([]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings`, settings);
      toast.success('Instellings gestoor!');
    } catch (error) {
      toast.error('Kon nie stoor nie');
    } finally {
      setSaving(false);
    }
  };

  const handleMigrateOriginals = async () => {
    setMigratingOriginals(true);
    setMigrationResult(null);
    try {
      const r = await axios.post(`${API}/admin/books/migrate-originals`);
      setMigrationResult(r.data);
      const m = r.data.migrated?.length || 0;
      const u = r.data.unrecoverable?.length || 0;
      const f = r.data.failed?.length || 0;
      if (m > 0) {
        toast.success(`${m} oorspronklike lêer${m === 1 ? '' : 's'} na Cloudinary gemigreer`);
      } else if (u === 0 && f === 0) {
        toast.info('Alle boeke is reeds opgeradeer');
      } else {
        toast.warning(`Geen lêers om te migreer nie (${u} onherwinbaar, ${f} mislukkings)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Migrasie het misluk');
    } finally {
      setMigratingOriginals(false);
    }
  };

  const [requestingReupload, setRequestingReupload] = useState(false);
  const handleRequestReupload = async () => {
    const unrecoverable = migrationResult?.unrecoverable || [];
    if (!unrecoverable.length) return;
    if (!window.confirm(`Stuur 'n e-pos versoek aan die skrywers van ${unrecoverable.length} boek${unrecoverable.length === 1 ? '' : 'e'} om hulle oorspronklike lêers weer op te laai?`)) {
      return;
    }
    setRequestingReupload(true);
    try {
      const r = await axios.post(`${API}/admin/books/request-reupload`, {
        book_ids: unrecoverable.map(u => u.id),
      });
      const { sent, skipped, failed } = r.data;
      if (sent > 0) {
        toast.success(`${sent} skrywer${sent === 1 ? '' : 's'} versoek om weer op te laai${skipped ? ` (${skipped} oorgeslaan)` : ''}${failed ? ` — ${failed} mislukkings` : ''}`);
      } else {
        toast.warning(`Geen versoeke gestuur nie (${skipped} oorgeslaan, ${failed} mislukkings)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kon nie versoeke stuur nie');
    } finally {
      setRequestingReupload(false);
    }
  };

  const handleAddGenre = async () => {
    if (!newGenre.trim()) {
      toast.error('Voer genre naam in');
      return;
    }

    if (genres.includes(newGenre.trim())) {
      toast.error('Genre bestaan alreeds');
      return;
    }

    setAddingGenre(true);
    try {
      await axios.post(`${API}/genres/${encodeURIComponent(newGenre.trim())}`);
      toast.success(`Genre "${newGenre}" bygevoeg!`);
      setNewGenre('');
      await fetchGenres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie genre byvoeg nie');
    } finally {
      setAddingGenre(false);
    }
  };

  const handleDeleteGenre = async (genre) => {
    if (!window.confirm(`Is jy seker jy wil "${genre}" verwyder?`)) {
      return;
    }

    setDeletingGenre(genre);
    try {
      await axios.delete(`${API}/genres/${encodeURIComponent(genre)}`);
      toast.success(`Genre "${genre}" verwyder!`);
      await fetchGenres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie genre verwyder nie');
    } finally {
      setDeletingGenre(null);
    }
  };

  // Cloudinary update function removed - credentials are now permanently configured

  const handleSaveEmailSettings = async () => {
    if (!emailSettings.api_key && !emailConfigured) {
      toast.error('API Sleutel is vereist');
      return;
    }
    if (!emailSettings.domain) {
      toast.error('Domein is vereist');
      return;
    }
    if (!emailSettings.sender_email) {
      toast.error('Sender E-pos is vereist');
      return;
    }

    setSavingEmail(true);
    try {
      await axios.post(`${API}/admin/email-settings`, {
        api_key: emailSettings.api_key || 'UNCHANGED',
        domain: emailSettings.domain,
        sender_email: emailSettings.sender_email,
        region: emailSettings.region || 'us'
      });
      toast.success('E-pos instellings gestoor!');
      setEmailConfigured(true);
      setEmailSettings(prev => ({ ...prev, api_key: '' })); // Clear the key from state
      await fetchEmailSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie e-pos instellings stoor nie');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/email-settings/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-pos toets het gefaal');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleDeleteEmailSettings = async () => {
    if (!window.confirm('Is jy seker jy wil die e-pos konfigurasie verwyder?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/email-settings`);
      toast.success('E-pos instellings verwyder');
      setEmailConfigured(false);
      setEmailSettings({ api_key: '', domain: '', sender_email: '', region: 'us' });
    } catch (error) {
      toast.error('Kon nie e-pos instellings verwyder nie');
    }
  };

  const handleResetBooksData = async () => {
    const confirmed = window.confirm(
      '⚠️ WAARSKUWING: Dit sal ALLE boeke, foute lyste, betalings en kennisgewings permanent verwyder!\n\n' +
      'Gebruikers en instellings sal behou word.\n\n' +
      'Is jy absoluut seker jy wil voortgaan?'
    );
    
    if (!confirmed) return;

    const doubleCheck = window.confirm(
      '🚨 FINALE BEVESTIGING:\n\n' +
      'Dit is jou laaste kans om terug te trek. Alle boekdata sal PERMANENT verwyder word.\n\n' +
      'Klik OK om voort te gaan of Kanselleer om te stop.'
    );

    if (!doubleCheck) return;

    setResettingBooks(true);
    try {
      const response = await axios.post(`${API}/admin/reset-books-data`);
      toast.success(
        `✅ ${response.data.message}\n\n` +
        `Verwyder: ${response.data.deleted.books} boeke, ${response.data.deleted.fault_lists} foute lyste, ` +
        `${response.data.deleted.payments} betalings, ${response.data.deleted.notifications} kennisgewings\n\n` +
        `${response.data.preserved}`,
        { duration: 8000 }
      );
      
      // Refresh page to show clean state
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Kon nie boekdata herstel nie';
      toast.error(errorMsg);
      console.error('Reset books data error:', error);
    } finally {
      setResettingBooks(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Instellings</h2>
        <p className="text-gray-600">Stelsel konfigurasie</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lees Website Publikasie</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="reading_website_url">Lees Website URL</Label>
            <Input
              id="reading_website_url"
              data-testid="reading-website-url-input"
              value={settings.reading_website_url}
              onChange={(e) => setSettings({ ...settings, reading_website_url: e.target.value })}
              placeholder="https://biblioteek.louisscheepers.com/admin/books/create"
            />
            <p className="text-sm text-gray-500 mt-1">URL waar gepubliseerde boeke gestuur word</p>
          </div>
        </CardContent>
      </Card>

      {currentUser?.is_main_admin && (
        <Card className="border-sky-200 bg-sky-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sky-700">
              <CloudUpload className="h-5 w-5" />
              Migreer Oorspronklike Lêers na Cloudinary
            </CardTitle>
            <CardDescription className="text-sky-700">
              Stoor enige oorspronklike PDF/DOCX/EPUB lêers wat nog plaaslik is na Cloudinary
              sodat hulle nie verloor word tydens herontplooiing nie. Boeke wat reeds gepubliseer
              is, word oorgeslaan. Lêers wat reeds verloor is, kan nie herstel word nie.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              data-testid="migrate-originals-btn"
              onClick={handleMigrateOriginals}
              disabled={migratingOriginals}
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              {migratingOriginals ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Migreer...</>
              ) : (
                <><CloudUpload className="h-4 w-4 mr-2" /> Skandeer en Migreer</>
              )}
            </Button>

            {migrationResult && (
              <div className="bg-white border rounded p-3 text-sm space-y-2" data-testid="migration-result">
                <p>
                  <strong>Geskandeer:</strong> {migrationResult.total_scanned} boeke
                  &nbsp;&middot;&nbsp;
                  <strong className="text-emerald-700">Gemigreer:</strong> {migrationResult.migrated?.length || 0}
                  &nbsp;&middot;&nbsp;
                  <strong className="text-amber-700">Reeds gemigreer:</strong> {migrationResult.already_migrated || 0}
                  &nbsp;&middot;&nbsp;
                  <strong className="text-red-700">Onherwinbaar:</strong> {migrationResult.unrecoverable?.length || 0}
                  &nbsp;&middot;&nbsp;
                  <strong className="text-red-700">Foute:</strong> {migrationResult.failed?.length || 0}
                </p>
                {migrationResult.migrated?.length > 0 && (
                  <div>
                    <p className="font-semibold text-emerald-700">Suksesvol gemigreer:</p>
                    <ul className="list-disc list-inside text-emerald-800 text-xs">
                      {migrationResult.migrated.map(m => <li key={m.id}>{m.boek_naam}</li>)}
                    </ul>
                  </div>
                )}
                {migrationResult.unrecoverable?.length > 0 && (
                  <div>
                    <p className="font-semibold text-red-700">Onherwinbaar (lêer reeds verlore):</p>
                    <ul className="list-disc list-inside text-red-800 text-xs">
                      {migrationResult.unrecoverable.map(u => <li key={u.id}>{u.boek_naam} — {u.reason}</li>)}
                    </ul>
                    <p className="text-xs text-gray-600 mt-1">
                      Hierdie boeke se oorspronklike lêers is reeds verlore. Die proefleser-leeser
                      sal terugval op die EPUB weergawe.
                    </p>
                    <Button
                      data-testid="request-reupload-btn"
                      onClick={handleRequestReupload}
                      disabled={requestingReupload}
                      size="sm"
                      className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {requestingReupload ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Stuur versoeke...</>
                      ) : (
                        <><Mail className="h-4 w-4 mr-2" /> Vra skrywers om weer op te laai ({migrationResult.unrecoverable.length})</>
                      )}
                    </Button>
                  </div>
                )}
                {migrationResult.failed?.length > 0 && (
                  <div>
                    <p className="font-semibold text-red-700">Foute:</p>
                    <ul className="list-disc list-inside text-red-800 text-xs">
                      {migrationResult.failed.map(f => <li key={f.id}>{f.boek_naam} — {f.reason}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {currentUser?.is_main_admin && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Users className="h-5 w-5" />
              Registrasie Limiete
            </CardTitle>
            <CardDescription className="text-purple-600">
              Beheer hoeveel proeflees en vertalers kan aansoek doen. Leë veld = Geen posisies beskikbaar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white p-4 rounded-lg border grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_proofreaders" className="flex items-center gap-2">
                  Maksimum Proeflees
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                    Tans: {registrationLimits.current_proofreaders}
                  </span>
                </Label>
                <Input
                  id="max_proofreaders"
                  data-testid="max-proofreaders-input"
                  type="number"
                  min="0"
                  value={registrationLimits.max_proofreaders}
                  onChange={(e) => setRegistrationLimits({ ...registrationLimits, max_proofreaders: e.target.value })}
                  placeholder="Leë = Gesluit"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {registrationLimits.max_proofreaders === '' || registrationLimits.max_proofreaders === null 
                    ? '⛔ Registrasie gesluit - geen posisies' 
                    : `✅ ${Math.max(0, parseInt(registrationLimits.max_proofreaders) - registrationLimits.current_proofreaders)} posisies beskikbaar`}
                </p>
              </div>
              <div>
                <Label htmlFor="max_translators" className="flex items-center gap-2">
                  Maksimum Vertalers
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                    Tans: {registrationLimits.current_translators}
                  </span>
                </Label>
                <Input
                  id="max_translators"
                  data-testid="max-translators-input"
                  type="number"
                  min="0"
                  value={registrationLimits.max_translators}
                  onChange={(e) => setRegistrationLimits({ ...registrationLimits, max_translators: e.target.value })}
                  placeholder="Leë = Gesluit"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {registrationLimits.max_translators === '' || registrationLimits.max_translators === null 
                    ? '⛔ Registrasie gesluit - geen posisies' 
                    : `✅ ${Math.max(0, parseInt(registrationLimits.max_translators) - registrationLimits.current_translators)} posisies beskikbaar`}
                </p>
              </div>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg text-sm text-purple-700">
              <p><strong>Let wel:</strong> Skrywers het geen limiet nie - hulle registrasies word altyd aanvaar (hangende goedkeuring).</p>
            </div>
            <Button
              onClick={handleSaveRegistrationLimits}
              disabled={savingLimits}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-registration-limits-btn"
            >
              {savingLimits ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Stoor Limiete</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Genre Bestuur</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Voeg Nuwe Genre By</Label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Voer genre naam in..."
                value={newGenre}
                onChange={(e) => setNewGenre(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGenre();
                  }
                }}
              />
              <Button
                onClick={handleAddGenre}
                disabled={addingGenre || !newGenre.trim()}
                className="bg-green-500 hover:bg-green-600"
              >
                {addingGenre ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Voeg By
                  </>
                )}
              </Button>
            </div>
          </div>

          <div>
            <Label>Beskikbare Genres ({genres.length})</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-96 overflow-y-auto border rounded-lg p-3">
              {genres.map((genre) => (
                <div
                  key={genre}
                  className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded border"
                >
                  <span className="text-sm font-medium text-gray-700">{genre}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteGenre(genre)}
                    disabled={deletingGenre === genre}
                    className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                  >
                    {deletingGenre === genre ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Klik op die vullisdrom ikoon om 'n genre te verwyder. Genres wat in gebruik is kan nie verwyder word nie.
            </p>
          </div>
        </CardContent>
      </Card>

      {currentUser?.is_main_admin && (
        <>
          <Card className={emailConfigured ? "border-green-200 bg-green-50" : "border-orange-200 bg-orange-50"}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${emailConfigured ? 'text-green-700' : 'text-orange-700'}`}>
                <Mail className="h-5 w-5" />
                E-pos Kennisgewings (Mailgun)
                {emailConfigured ? (
                  <CheckCircle className="h-5 w-5 ml-2" />
                ) : (
                  <XCircle className="h-5 w-5 ml-2" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {emailConfigured ? (
                <div className="flex items-center gap-2 text-green-700 mb-4">
                  <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">E-pos kennisgewings is aktief</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-orange-700 mb-4">
                  <div className="h-3 w-3 bg-orange-500 rounded-full"></div>
                  <span className="font-medium">E-pos kennisgewings is nie gekonfigureer nie</span>
                </div>
              )}
              
              <div className="bg-white p-4 rounded-lg border">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="mailgun_api_key">Mailgun API Sleutel</Label>
                    {emailConfigured && (
                      <p className="text-xs text-green-700 mt-1 mb-2 bg-green-50 border border-green-200 rounded p-2">
                        ✓ Jou Mailgun sleutel is reeds gestoor. <strong>Laat hierdie veld leeg</strong> as jy dit nie wil verander nie. Moenie jou wagwoord hier inplak nie!
                      </p>
                    )}
                    <div className="flex gap-2 mt-1">
                      {/* Hidden dummy field to absorb browser auto-fill */}
                      <input type="text" name="prevent_autofill_user" autoComplete="username" style={{ display: 'none' }} aria-hidden="true" />
                      <input type="password" name="prevent_autofill_pass" autoComplete="current-password" style={{ display: 'none' }} aria-hidden="true" />
                      <Input
                        id="mailgun_api_key"
                        name="mailgun_api_sleutel_unique"
                        type={showApiKey ? "text" : "password"}
                        value={emailSettings.api_key}
                        onChange={(e) => setEmailSettings({ ...emailSettings, api_key: e.target.value })}
                        placeholder={emailConfigured ? "Laat leeg om bestaande te behou" : "key-xxxxxxxxxxxxxxxx"}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        data-testid="mailgun-api-key-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Kry dit by Mailgun Dashboard → Settings → API Keys
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="mailgun_domain">Mailgun Domein</Label>
                    <Input
                      id="mailgun_domain"
                      value={emailSettings.domain}
                      onChange={(e) => setEmailSettings({ ...emailSettings, domain: e.target.value })}
                      placeholder="mg.joudomain.com of sandbox123.mailgun.org"
                      data-testid="mailgun-domain-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Jou geverifieerde Mailgun domein
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="mailgun_sender">Sender E-pos Adres</Label>
                    <Input
                      id="mailgun_sender"
                      type="email"
                      value={emailSettings.sender_email}
                      onChange={(e) => setEmailSettings({ ...emailSettings, sender_email: e.target.value })}
                      placeholder="noreply@joudomain.com"
                      data-testid="mailgun-sender-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Die e-pos adres wat in die "Van" veld sal verskyn
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="mailgun_region">Mailgun Streek</Label>
                    <select
                      id="mailgun_region"
                      value={emailSettings.region || 'us'}
                      onChange={(e) => setEmailSettings({ ...emailSettings, region: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="mailgun-region-select"
                    >
                      <option value="us">🇺🇸 US (api.mailgun.net)</option>
                      <option value="eu">🇪🇺 EU (api.eu.mailgun.net)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Kyk in jou Mailgun dashboard onder <strong>Sending → Domains → {emailSettings.domain || 'jou domein'}</strong> watter streek dit toon. As jy ’n <strong>403 Forbidden</strong> fout kry, is dit gewoonlik die verkeerde streek.
                    </p>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={handleSaveEmailSettings}
                      disabled={savingEmail}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid="save-email-settings-btn"
                    >
                      {savingEmail ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                      ) : (
                        <><Save className="h-4 w-4 mr-2" />Stoor</>
                      )}
                    </Button>
                    
                    {emailConfigured && (
                      <>
                        <Button
                          onClick={handleTestEmail}
                          disabled={testingEmail}
                          variant="outline"
                          data-testid="test-email-btn"
                        >
                          {testingEmail ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Toets...</>
                          ) : (
                            <><Send className="h-4 w-4 mr-2" />Stuur Toets</>
                          )}
                        </Button>
                        
                        <Button
                          onClick={handleDeleteEmailSettings}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          data-testid="delete-email-settings-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />Verwyder
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Wanneer gekonfigureer, sal e-pos kennisgewings outomaties gestuur word wanneer:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
                <li>Vertaler aansoeke goedgekeur of afgekeur word</li>
                <li>Boek aanvrae goedgekeur of afgekeur word</li>
                <li>Skrywer of proefleser aansoeke goedgekeur word</li>
              </ul>
            </CardContent>
          </Card>

          {/* Payment Rates Settings */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <DollarSign className="h-5 w-5" />
                Betalingstariewe & Besigheidsreëls
              </CardTitle>
              <CardDescription className="text-blue-600">
                Konfigureer betalingstariewe en besigheidsreëls vir die platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingPaymentRates ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : (
                <>
                  {/* Proofreader Rates */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      📖 Proefleser Tariewe
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="proofreader_rate">Tarief per Fout (R)</Label>
                        <Input
                          id="proofreader_rate"
                          type="number"
                          step="0.50"
                          min="0"
                          value={paymentRates.proofreader_rate_per_error}
                          onChange={(e) => handlePaymentRateChange('proofreader_rate_per_error', e.target.value)}
                          data-testid="proofreader-rate-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Proefleser word per goedgekeurde fout betaal
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Translator Rates */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      🌐 Vertaler Tariewe
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="translator_min_rate">Minimum Tarief (R)</Label>
                        <Input
                          id="translator_min_rate"
                          type="number"
                          step="10"
                          min="0"
                          value={paymentRates.translator_min_rate}
                          onChange={(e) => handlePaymentRateChange('translator_min_rate', e.target.value)}
                          data-testid="translator-min-rate-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Vir boeke met min. {paymentRates.translator_min_pages} bladsye of minder
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="translator_max_rate">Maksimum Tarief (R)</Label>
                        <Input
                          id="translator_max_rate"
                          type="number"
                          step="10"
                          min="0"
                          value={paymentRates.translator_max_rate}
                          onChange={(e) => handlePaymentRateChange('translator_max_rate', e.target.value)}
                          data-testid="translator-max-rate-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Vir boeke met maks. {paymentRates.translator_max_pages} bladsye of meer
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="translator_min_pages">Min. Bladsye vir Tarief</Label>
                        <Input
                          id="translator_min_pages"
                          type="number"
                          min="1"
                          value={paymentRates.translator_min_pages}
                          onChange={(e) => handlePaymentRateChange('translator_min_pages', e.target.value)}
                          data-testid="translator-min-pages-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="translator_max_pages">Maks. Bladsye vir Tarief</Label>
                        <Input
                          id="translator_max_pages"
                          type="number"
                          min="1"
                          value={paymentRates.translator_max_pages}
                          onChange={(e) => handlePaymentRateChange('translator_max_pages', e.target.value)}
                          data-testid="translator-max-pages-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="translator_deduction">Aftrekking per Fout (R)</Label>
                        <Input
                          id="translator_deduction"
                          type="number"
                          step="0.50"
                          min="0"
                          value={paymentRates.translator_deduction_per_error}
                          onChange={(e) => handlePaymentRateChange('translator_deduction_per_error', e.target.value)}
                          data-testid="translator-deduction-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Vertaler word per fout afgetrek (na foute lys goedkeuring)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Business Rules */}
                  <div className="bg-white p-4 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      Besigheidsreëls
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="max_faults" className="flex items-center gap-2">
                          Maksimum Foute per Boek
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Belangrik</span>
                        </Label>
                        <Input
                          id="max_faults"
                          type="number"
                          min="1"
                          value={paymentRates.max_faults_allowed}
                          onChange={(e) => handlePaymentRateChange('max_faults_allowed', e.target.value)}
                          data-testid="max-faults-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          As 'n boek meer foute het, moet dit herdoen word deur die skrywer/vertaler
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="rejection_threshold" className="flex items-center gap-2">
                          <Ban className="h-4 w-4 text-red-500" />
                          Afkeur Drempel vir Skorsing
                        </Label>
                        <Input
                          id="rejection_threshold"
                          type="number"
                          min="1"
                          value={paymentRates.rejection_threshold}
                          onChange={(e) => handlePaymentRateChange('rejection_threshold', e.target.value)}
                          data-testid="rejection-threshold-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Vertalers word outomaties geskors na hierdie aantal afkeure
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Admin Payment */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      👤 Admin Betaling
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="admin_monthly_payment">Admin Maandelikse Betaling (R)</Label>
                        <Input
                          id="admin_monthly_payment"
                          type="number"
                          step="100"
                          min="0"
                          value={paymentRates.admin_monthly_payment}
                          onChange={(e) => handlePaymentRateChange('admin_monthly_payment', e.target.value)}
                          data-testid="admin-monthly-payment-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Vaste maandelikse bedrag vir admin (0 = nie toepaslik)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-blue-100 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">Opsomming</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Proefleser verdien <strong>R{paymentRates.proofreader_rate_per_error}</strong> per goedgekeurde fout</li>
                      <li>• Vertaler verdien tussen <strong>R{paymentRates.translator_min_rate}</strong> en <strong>R{paymentRates.translator_max_rate}</strong> gebaseer op bladsy telling</li>
                      <li>• Vertaler verloor <strong>R{paymentRates.translator_deduction_per_error}</strong> per vertaalfout</li>
                      <li>• Boeke met meer as <strong>{paymentRates.max_faults_allowed}</strong> foute moet herdoen word</li>
                      <li>• Vertalers word geskors na <strong>{paymentRates.rejection_threshold}</strong> afkeure</li>
                    </ul>
                  </div>

                  {/* Save Button */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={handleSavePaymentRates}
                      disabled={savingPaymentRates}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="save-payment-rates-btn"
                    >
                      {savingPaymentRates ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                      ) : (
                        <><Save className="h-4 w-4 mr-2" />Stoor Betalingstariewe</>
                      )}
                    </Button>
                    
                    <Button
                      onClick={toggleAuditHistory}
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="toggle-audit-history-btn"
                    >
                      <History className="h-4 w-4 mr-2" />
                      {showAuditHistory ? 'Verberg' : 'Wys'} Geskiedenis
                    </Button>
                  </div>

                  {/* Audit Trail Panel */}
                  {showAuditHistory && (
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg border">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <History className="h-4 w-4" />
                        Tariewe Verandering Geskiedenis
                      </h4>
                      {loadingAudit ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                        </div>
                      ) : auditHistory.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Geen veranderinge aangeteken nie</p>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {auditHistory.map((record, idx) => (
                            <div key={record.id || idx} className="bg-white p-3 rounded border text-sm">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-gray-700">{record.changed_by}</span>
                                <span className="text-xs text-gray-500">
                                  {new Date(record.timestamp).toLocaleString('af-ZA')}
                                </span>
                              </div>
                              <ul className="space-y-1">
                                {record.changes?.map((change, cIdx) => (
                                  <li key={cIdx} className="flex items-center gap-2 text-gray-600">
                                    <span className="font-medium">{formatFieldName(change.field)}:</span>
                                    <span className="text-red-500 line-through">{change.old_value ?? 'N/A'}</span>
                                    <span>→</span>
                                    <span className="text-green-600 font-medium">{change.new_value}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Earnings Settings */}
          <Card className="border-green-200 bg-green-50 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <DollarSign className="h-5 w-5" />
                Verdienste Instellings
              </CardTitle>
              <CardDescription className="text-green-600">
                Konfigureer munt-na-Rand koers en inkomste verdeling persentasies
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingEarnings ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-green-500" />
                </div>
              ) : (
                <>
                  {/* Coin to Rand Rate */}
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <Label className="text-green-800 font-semibold">Munt na Rand Koers</Label>
                    <p className="text-xs text-gray-500 mb-2">Hoeveel Rand is elke munt werd?</p>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-green-700">R</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={earningsSettings.coin_to_rand_rate}
                        onChange={(e) => setEarningsSettings({...earningsSettings, coin_to_rand_rate: parseFloat(e.target.value) || 0})}
                        className="w-32"
                        data-testid="coin-rate-input"
                      />
                      <span className="text-sm text-gray-500">per munt</span>
                    </div>
                  </div>

                  {/* Revenue Split Percentages */}
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <Label className="text-green-800 font-semibold">Inkomste Verdeling</Label>
                    <p className="text-xs text-gray-500 mb-4">Persentasies moet 100% wees</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Google/Apple</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={earningsSettings.google_apple_percentage}
                            onChange={(e) => setEarningsSettings({...earningsSettings, google_apple_percentage: parseFloat(e.target.value) || 0})}
                            className="w-20"
                            data-testid="google-pct-input"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-gray-600">Die Biblioteek (App)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={earningsSettings.app_percentage}
                            onChange={(e) => setEarningsSettings({...earningsSettings, app_percentage: parseFloat(e.target.value) || 0})}
                            className="w-20"
                            data-testid="app-pct-input"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-gray-600">Skrywers</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={earningsSettings.writer_percentage}
                            onChange={(e) => setEarningsSettings({...earningsSettings, writer_percentage: parseFloat(e.target.value) || 0})}
                            className="w-20"
                            data-testid="writer-pct-input"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Total indicator */}
                    <div className="mt-4 p-2 bg-gray-100 rounded flex items-center justify-between">
                      <span className="text-sm font-medium">Totaal:</span>
                      <span className={`text-lg font-bold ${
                        Math.abs((earningsSettings.google_apple_percentage + earningsSettings.app_percentage + earningsSettings.writer_percentage) - 100) < 0.01
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {(earningsSettings.google_apple_percentage + earningsSettings.app_percentage + earningsSettings.writer_percentage).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Example Calculation */}
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <Label className="text-yellow-800 font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Voorbeeld Berekening
                    </Label>
                    <p className="text-xs text-gray-600 mt-2">
                      As 'n skrywer <strong>1000 munte</strong> verdien:
                    </p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p>Totaal: <strong>R{(1000 * earningsSettings.coin_to_rand_rate).toFixed(2)}</strong></p>
                      <p className="text-gray-500">→ Google/Apple ({earningsSettings.google_apple_percentage}%): R{(1000 * earningsSettings.coin_to_rand_rate * earningsSettings.google_apple_percentage / 100).toFixed(2)}</p>
                      <p className="text-orange-600">→ Die Biblioteek ({earningsSettings.app_percentage}%): R{(1000 * earningsSettings.coin_to_rand_rate * earningsSettings.app_percentage / 100).toFixed(2)}</p>
                      <p className="text-green-600 font-semibold">→ Skrywer ({earningsSettings.writer_percentage}%): R{(1000 * earningsSettings.coin_to_rand_rate * earningsSettings.writer_percentage / 100).toFixed(2)}</p>
                    </div>
                  </div>

                  <Button 
                    onClick={handleSaveEarningsSettings}
                    disabled={savingEarnings || Math.abs((earningsSettings.google_apple_percentage + earningsSettings.app_percentage + earningsSettings.writer_percentage) - 100) > 0.01}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-earnings-settings-btn"
                  >
                    {savingEarnings ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-2" />Stoor Verdienste Instellings</>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50 mb-6">
            <CardHeader>
              <CardTitle className="text-green-700">
                ☁️ Cloudinary Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-green-700">
                <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium">Cloudinary is gekonfigureer en aktief</span>
              </div>
              <p className="text-sm text-green-600 mt-2">
                Alle nuwe boek voorblaaie word outomaties na Cloudinary opgelaai vir permanente stoor.
              </p>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Gevaar Sone - Herstel Boekdata
              </CardTitle>
            </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-red-200">
              <p className="text-sm text-gray-700 mb-3">
                <strong>⚠️ Waarskuwing:</strong> Hierdie aksie sal <strong className="text-red-600">ALLE boeke, foute lyste, betalings en kennisgewings permanent verwyder</strong>.
              </p>
              <p className="text-sm text-gray-700 mb-3">
                ✅ <strong>Sal behou word:</strong> Gebruikers (skrywers, proeflees, admins), instellings, genres
              </p>
              <p className="text-sm text-gray-700 mb-4">
                Gebruik dit slegs as jy van voor af wil begin met skoon boekdata.
              </p>
              <Button
                onClick={handleResetBooksData}
                disabled={resettingBooks}
                variant="destructive"
                className="w-full sm:w-auto"
              >
                {resettingBooks ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Besig om te herstel...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Herstel Alle Boekdata
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      <Button
        data-testid="save-settings-btn"
        onClick={handleSave}
        disabled={saving}
        className="bg-orange-500 hover:bg-orange-600"
      >
        {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Besig...</> : <><Save className="mr-2 h-4 w-4" />Stoor Instellings</>}
      </Button>

      <SocialMediaSettings />
    </div>
  );
};

export default AdminSettings;